import { useRef } from "react"
import { Container } from "@/components/layout/Container"
import { Eyebrow } from "@/components/shared/Eyebrow"
import { Button } from "@/components/ui/button"
import AddToCartButton from "@/features/checkout/AddToCartButton"
import { getDisplayPrice, formatMXN, promotionFromActive } from "@/content/prices"
import { scrollToNextSection } from "@/lib/utils"
import type { ServiceProps } from "@/content/services"
import type { ServiceContent } from "@/content/services-content"
import { useActivePromotionForItem } from "@/features/promotions/ActivePromotionProvider"

interface ServiceHeroProps {
	service: ServiceProps
	content?: ServiceContent
}

/**
 * Hero de la ficha de servicio: mismo patrón que `ProductHero` (Eyebrow, precio,
 * `AddToCartButton`, scroll a la siguiente sección) pero sin foto — los servicios
 * (consulta remota, dieta personalizada) no tienen una imagen de producto, así
 * que el hero es texto centrado únicamente.
 */
export function ServiceHero({ service, content }: ServiceHeroProps): React.JSX.Element {
	const sectionRef = useRef<HTMLElement>(null)
	const promo = promotionFromActive(useActivePromotionForItem(service.id))
	const price = getDisplayPrice(service.price, promo, service.id)
	const cartItem = { id: service.id, priceId: service.priceId, amount: 1 }

	return (
		<section ref={sectionRef} className="relative isolate overflow-hidden bg-background">
			<div aria-hidden className="pointer-events-none absolute inset-0 bg-radial-glow" />

			<Container className="relative flex flex-col items-center pt-6 pb-16 text-center md:py-24">
				<h1 className="mt-0 text-balance text-3xl font-bold tracking-tight sm:text-4xl">{service.name}</h1>

				{content?.slogan && (
					<Eyebrow align="center" className="mt-2">
						{content.slogan}
					</Eyebrow>
				)}

				<div className="mt-4 flex items-baseline gap-3">
					{price.hasDiscount && (
						<span className="text-lg text-foreground line-through">{formatMXN(price.original)}</span>
					)}
					<span className="text-3xl font-bold text-primary">{formatMXN(price.final)}</span>
				</div>

				<p className="mt-1 max-w-md text-sm text-muted-foreground">{service.description}</p>

				<div className="mt-8 flex flex-col items-center gap-4">
					<AddToCartButton cartItem={cartItem} variant="default" size="lg" className="w-full sm:w-auto" />
					<Button variant="link" onClick={() => scrollToNextSection(sectionRef.current)}>
						Leer más
					</Button>
				</div>
			</Container>
		</section>
	)
}
